import React from 'react'; // Cache busting v4 - Finance Edit Modal Fix
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LandingPage } from './pages/LandingPage';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { PendingAccessPage } from './pages/PendingAccessPage';
import { MembersPage } from './pages/MembersPage';
import MemberDetailPage from './pages/MemberDetailPage';
import { NewcomerPage } from './pages/NewcomerPage';
import { VisitorsPage } from './pages/VisitorsPage';
import { ProfilePage } from './pages/ProfilePage';
import { FinancePage } from './pages/FinancePage';
import AdminPage from './pages/AdminPage';
import { CarryoverFundsPage } from './pages/CarryoverFundsPage';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { MainLayout } from './components/common/MainLayout';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { DiscipleshipPage } from './pages/DiscipleshipPage';
import { DiscipleshipStudentManagementPage } from './pages/DiscipleshipStudentManagementPage';
import VisitationPage from './pages/VisitationPage';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';

// 인증된 사용자를 위한 레이아웃
const AuthenticatedLayout: React.FC = () => {
  const { user, isPendingAccess } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return isPendingAccess ? <Navigate to="/pending-approval" replace /> : <MainLayout />;
};

const PendingAccessRoute: React.FC = () => {
  const { user, isPendingAccess, accessError } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!isPendingAccess) return <Navigate to="/home" replace />;
  return <PendingAccessPage email={user.email} hasError={accessError} />;
};

// 앱 라우트 설정
const AppRoutes: React.FC = () => {
  const { user } = useAuth();
  return (
    <Routes>
      {/* 랜딩 페이지 - 로그인하지 않은 사용자에게 보여줌 */}
      <Route path="/" element={user ? <Navigate to="/home" replace /> : <LandingPage />} />

      {/* 보호된 라우트들 */}
      <Route element={<AuthenticatedLayout />}>
        <Route path="/home" element={<ProtectedRoute requiredPage="dashboard"><HomePage /></ProtectedRoute>} />
        <Route path="/members" element={<ProtectedRoute requiredPage="members"><MembersPage /></ProtectedRoute>} />
        <Route path="/members/:memberId" element={<ProtectedRoute requiredPage="members"><MemberDetailPage /></ProtectedRoute>} />
        <Route path="/newcomers" element={<ProtectedRoute requiredPage="newcomers"><NewcomerPage /></ProtectedRoute>} />
        <Route path="/visitors" element={<ProtectedRoute requiredPage="visitors"><VisitorsPage /></ProtectedRoute>} />
        <Route path="/finance" element={<ProtectedRoute requiredPage="finance"><FinancePage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/discipleship" element={<ProtectedRoute requiredPage="discipleship"><DiscipleshipPage /></ProtectedRoute>} />
        <Route path="/discipleship/:classId/students" element={<ProtectedRoute requiredPage="discipleship"><DiscipleshipStudentManagementPage /></ProtectedRoute>} />
        <Route path="/visitations" element={<ProtectedRoute requiredPage="visitations"><VisitationPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute requiredPage="admin"><AdminPage /></ProtectedRoute>} />
        <Route path="/carryover-funds" element={<ProtectedRoute requiredPage="finance"><CarryoverFundsPage /></ProtectedRoute>} />
      </Route>

      {/* 로그인 페이지 */}
      <Route path="/login" element={user ? <Navigate to="/home" replace /> : <LoginPage />} />
      <Route path="/signup" element={user ? <Navigate to="/home" replace /> : <SignupPage />} />
      <Route path="/pending-approval" element={<PendingAccessRoute />} />
    </Routes>
  );
};

// 메인 App 컴포넌트
function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <NotificationProvider>
          <Router>
            <AppRoutes />
          </Router>
        </NotificationProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
